import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

const RETRY_DELAY_MS = 1500;

interface UseVideoRetryResult {
  hasError: boolean;
  isRetrying: boolean;
  manualRetry: () => void;
}

/**
 * Automatic + manual retry for `<video>` load errors.
 *
 * - On first error: waits `RETRY_DELAY_MS` then calls `video.load()` (1 auto-retry).
 * - If retry also fails: sets `hasError = true` for fallback UI.
 * - `manualRetry()` resets everything and triggers a fresh `video.load()`.
 * - All state resets when `src` changes.
 */
export function useVideoRetry(
  videoEl: HTMLVideoElement | null,
  src: string | undefined,
  maxRetries = 1
): UseVideoRetryResult {
  const [hasError, setHasError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const retryCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Reset state when src changes
  useEffect(() => {
    retryCountRef.current = 0;
    setHasError(false);
    setIsRetrying(false);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [src]);

  // Listen for error events on the video element
  useEffect(() => {
    if (!videoEl || !src) return;

    const onError = () => {
      setIsRetrying(false);

      if (retryCountRef.current < maxRetries) {
        // Auto-retry
        retryCountRef.current += 1;
        setIsRetrying(true);

        Sentry.addBreadcrumb({
          category: 'video-player',
          message: `Auto-retrying video load (attempt ${retryCountRef.current})`,
          level: 'warning',
          data: { src },
        });

        timerRef.current = setTimeout(() => {
          videoEl.load();
          setIsRetrying(false);
        }, RETRY_DELAY_MS);
      } else {
        // Retries exhausted
        setHasError(true);

        Sentry.captureMessage('Video load failed after retries', {
          level: 'error',
          extra: { src, retryCount: retryCountRef.current },
        });
      }
    };

    videoEl.addEventListener('error', onError);
    return () => {
      videoEl.removeEventListener('error', onError);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [videoEl, src, maxRetries]);

  const manualRetry = useCallback(() => {
    if (!videoEl) return;

    retryCountRef.current = 0;
    setHasError(false);
    setIsRetrying(true);

    Sentry.addBreadcrumb({
      category: 'video-player',
      message: 'Manual video retry triggered',
      level: 'info',
      data: { src },
    });

    timerRef.current = setTimeout(() => {
      videoEl.load();
      setIsRetrying(false);
    }, RETRY_DELAY_MS);
  }, [videoEl, src]);

  return { hasError, isRetrying, manualRetry };
}
